/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { formatDateSafely } from '@/utils/dateUtils';

type FlattenOptions = {
  excludeKeys?: string[];
};

// Helper function to check if a field is a date field
function isDateField(key: string): boolean {
  const dateFields = [
    'start_date',
    'end_date',
    'date',
    'created_at',
    'updated_at',
  ];
  return dateFields.includes(key);
}

// Helper function to check if a string is in ISO date format
function isISODateString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
}

function flattenFormData<T extends Record<string, any>>(
  data: T,
  options?: FlattenOptions,
): Partial<T> {
  const { excludeKeys = [] } = options || {};

  function flatten(value: any, key?: string): any {
    // Check if it's a Day.js object and format it
    if (value && typeof value === 'object' && value.$isDayjsObject === true) {
      return formatDateSafely(value);
    }

    // Check if it's a Date object and format it
    if (value instanceof Date) {
      return formatDateSafely(value);
    }

    // Check if it's a date string in ISO format and convert to YYYY-MM-DD
    if (
      typeof value === 'string' &&
      key &&
      isDateField(key) &&
      isISODateString(value)
    ) {
      return formatDateSafely(value);
    }

    if (Array.isArray(value)) {
      // Só aplica a regra para arrays de objetos com id se NÃO for 'translations'
      if (
        key !== 'translations' &&
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        'id' in value[0]
      ) {
        return value.map((item: any) => item.id);
      }
      return value.map((item) => flatten(item));
    }
    if (value && typeof value === 'object') {
      if ('id' in value && Object.keys(value).length <= 2) {
        return value.id;
      }
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, flatten(v, k)]),
      );
    }
    return value;
  }

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (excludeKeys.includes(key)) {
        return [key, value];
      }
      return [key, flatten(value, key)];
    }),
  ) as Partial<T>;
}

export function usePrepareSubmit<T extends Record<string, any>>(
  options?: FlattenOptions,
) {
  return useCallback((data: T) => flattenFormData(data, options), [options]);
}
